export const bfgIntergroupPmtilesLayerId = 'bfg-pmtiles-cells-intergroup';
const bfgLeaderTeamIdFeatureStateKey = 'leaderTeamId';
const bfgNoLeaderId = -1;
const bfgNoMyTeamSentinel = -2;
export const bfgIntergroupGreyColor = '#E4E7EB';
// "Vue intergroupe": solid green, reserved exclusively for the viewer's own company, so it always
// pops out from the crowd regardless of how many other teams/colors are on screen.
export const bfgIntergroupMineColor = '#2E7D32';

// Every *other* team gets its own color too — the backend's own `team.color` field turned out to
// be the same value for every team, so this generates a stable, soft/pastel, mutually-
// distinguishable palette instead, keyed by each team's rank among the other teams (sorted by id,
// so it's stable across re-renders without needing to persist an assignment anywhere). Green is
// deliberately excluded so nothing here is ever confused with "mine". Kept pastel/muted on purpose
// — these only ever show up as outlines (hex border, circle-marker ring), never as a fill, so they
// read as identity accents rather than competing for attention with the solid "mine" green.
const bfgIntergroupOtherTeamPalette = ['#93C5FD',
// pastel blue
'#FCD34D',
// pastel amber
'#F9A8D4',
// pastel pink
'#C4B5FD',
// pastel violet
'#67E8F9',
// pastel cyan
'#FDBA74',
// pastel orange
'#A5B4FC',
// pastel indigo
'#FCA5A5' // pastel red
];

/** Stable per-team color, reserving bfgIntergroupMineColor exclusively for the viewer's own team. */
export function getBfgIntergroupTeamColor(teamId, teams, myTeamId) {
  if (teamId === myTeamId) return bfgIntergroupMineColor;
  const otherTeamIds = teams.map(team => team.id).filter(id => id !== myTeamId).sort((a, b) => a - b);
  const index = otherTeamIds.indexOf(teamId);
  return bfgIntergroupOtherTeamPalette[index === -1 ? 0 : index % bfgIntergroupOtherTeamPalette.length];
}
/**
 * bfg_2026 tile properties carry per-team breakdowns as stringified JSON objects
 * (score_by_team, commuting_passages_by_team) — this picks out whichever team currently has the
 * most passages on a given hex (the "leading" team for that zone).
 */
export function getBfgLeader(properties, mode) {
  const propertyKey = mode === 'commuting' ? 'commuting_passages_by_team' : 'score_by_team';
  const raw = properties?.[propertyKey];
  if (typeof raw !== 'string') return {
    teamId: null,
    value: 0
  };
  try {
    const parsed = JSON.parse(raw);
    let bestTeamId = null;
    let bestValue = 0;
    for (const [key, rawValue] of Object.entries(parsed)) {
      const value = Number(rawValue) || 0;
      if (value > bestValue) {
        bestValue = value;
        bestTeamId = Number(key);
      }
    }
    return {
      teamId: bestTeamId,
      value: bestValue
    };
  } catch {
    return {
      teamId: null,
      value: 0
    };
  }
}

/**
 * All teams' raw values on a hex (not just the leader) — used for the click popup's full ranking.
 * Keyed by team id (as a string, since that's how it comes out of JSON.parse on an object key).
 */
export function getBfgAllTeamValues(properties, mode) {
  const propertyKey = mode === 'commuting' ? 'commuting_passages_by_team' : 'score_by_team';
  const raw = properties?.[propertyKey];
  if (typeof raw !== 'string') return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

// Style expressions can't parse JSON properties themselves, so this feeds the feature-state that
// the fill/border expressions below read.
export function computeBfgIntergroupFeatureState(properties, mode) {
  const {
    teamId
  } = getBfgLeader(properties, mode);
  return {
    [bfgLeaderTeamIdFeatureStateKey]: teamId ?? bfgNoLeaderId
  };
}

// "Vue intergroupe" fill: one color per team that currently leads a hex — mine reserved green,
// every other team its own pastel from the generated palette — fully transparent where nobody
// leads yet so untouched territory doesn't get confused with anyone's claim.
export function buildBfgIntergroupFillColorExpression(teams, myTeamId) {
  if (teams.length === 0) return bfgIntergroupGreyColor;
  return ['match', ['coalesce', ['feature-state', bfgLeaderTeamIdFeatureStateKey], bfgNoLeaderId], ...teams.flatMap(team => [team.id, getBfgIntergroupTeamColor(team.id, teams, myTeamId)]), bfgIntergroupGreyColor];
}
export function buildBfgIntergroupBorderColorExpression(teams, myTeamId) {
  if (teams.length === 0) return 'rgba(0,0,0,0)';
  return ['case', ['==', ['coalesce', ['feature-state', bfgLeaderTeamIdFeatureStateKey], bfgNoLeaderId], bfgNoLeaderId], 'rgba(0,0,0,0)', ['match', ['coalesce', ['feature-state', bfgLeaderTeamIdFeatureStateKey], bfgNoLeaderId], ...teams.flatMap(team => [team.id, getBfgIntergroupTeamColor(team.id, teams, myTeamId)]), 'rgba(0,0,0,0)']];
}
export function buildBfgIntergroupFillOpacityExpression(teams, myTeamId) {
  if (teams.length === 0) return 0;
  return ['match', ['coalesce', ['feature-state', bfgLeaderTeamIdFeatureStateKey], bfgNoLeaderId], ...teams.flatMap(team => [team.id, team.id === myTeamId ? 0.82 : 0.55]), 0];
}

// Mine also gets a thicker border (on top of the usual hover bump), a second cue reinforcing it
// over the other teams' hexes.
export function buildBfgIntergroupBorderWidthExpression(myTeamId) {
  return ['interpolate', ['linear'], ['zoom'], 11, ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, ['==', ['coalesce', ['feature-state', bfgLeaderTeamIdFeatureStateKey], bfgNoLeaderId], myTeamId ?? bfgNoMyTeamSentinel], 2, 0.5], 18, ['case', ['boolean', ['feature-state', 'hover'], false], 5, ['==', ['coalesce', ['feature-state', bfgLeaderTeamIdFeatureStateKey], bfgNoLeaderId], myTeamId ?? bfgNoMyTeamSentinel], 4, 1.5]];
}

// "Vue intergroupe" also shows one circle marker per hex, filled in the *leading* team's own
// color (mine green, every other team its own pastel) with a thin white ring — same idea as the
// hex fill itself. Points carry plain `fillColor`/`strokeColor` properties (resolved when the
// point is built — see exploration-map-game-intergroup.tsx), so the layer just `get`s them.
export const bfgIntergroupTeamCircleFillColorExpression = ['get', 'fillColor'];
export const bfgIntergroupTeamCircleStrokeColorExpression = ['get', 'strokeColor'];

// Exact same zoom-to-pixel-radius curve as the game's own already-solved "team colors" mode
// circle markers (gameTilesLevelsLayerId in hooks/map/game-tiles.ts) — a linear px size over a
// wide 11→18 zoom span, instead of the solo map's icons (exponential, only defined over 11→13),
// which is what let those get oversized relative to their hex once zoomed out past that range.
const bfgIntergroupTeamCircleRadiusAtZoom = {
  at11: 5,
  at18: 30
};
export const bfgIntergroupTeamCircleRadiusExpression = ['interpolate', ['linear'], ['zoom'], 11, bfgIntergroupTeamCircleRadiusAtZoom.at11, 18, bfgIntergroupTeamCircleRadiusAtZoom.at18];

// `strokeWidthFactor` lets a point request a thicker-than-default ring if needed (see
// exploration-map-game-intergroup.tsx) — currently every point uses factor 1 (the plain curve).
export const bfgIntergroupTeamCircleStrokeWidthExpression = ['interpolate', ['linear'], ['zoom'], 11, ['*', 1, ['coalesce', ['get', 'strokeWidthFactor'], 1]], 18, ['*', 2, ['coalesce', ['get', 'strokeWidthFactor'], 1]]];

// How much of the circle's diameter the team icon (when there is one) should fill — 1 covers it
// edge-to-edge, matching its stroke exactly.
const bfgIntergroupTeamIconFillRatio = 1;

// Team icon uploads arrive in arbitrary sizes/aspect ratios, so before being handed to MapLibre
// they're pre-rendered — cover-fit, then clipped to a circle — onto a canvas of this fixed size
// (see createCircularIconImageData in exploration-map-game-intergroup.tsx). Round like every other
// team/company avatar in the app (GeogroupAvatar, partner icons, …), and every processed icon ends
// up the exact same pixel size, so a single flat `icon-size` curve fits all of them — no per-team
// factor needed.
export const bfgIntergroupTeamIconCanvasSize = 128;
const bfgIntergroupTeamIconSizeAtZoom = {
  at11: 2 * bfgIntergroupTeamCircleRadiusAtZoom.at11 * bfgIntergroupTeamIconFillRatio / bfgIntergroupTeamIconCanvasSize,
  at18: 2 * bfgIntergroupTeamCircleRadiusAtZoom.at18 * bfgIntergroupTeamIconFillRatio / bfgIntergroupTeamIconCanvasSize
};

// Same 11→18 zoom span as the circle radius it sits on top of, so the icon grows/shrinks exactly
// in step with its circle and never spills past it.
export const bfgIntergroupTeamIconSizeExpression = ['interpolate', ['linear'], ['zoom'], 11, bfgIntergroupTeamIconSizeAtZoom.at11, 18, bfgIntergroupTeamIconSizeAtZoom.at18];
export const bfgIntergroupTeamIconImageExpression = ['get', 'iconId'];